<template>
    <form ref="form" v-on="formEvents">
        <div v-if="system" class="text-center alert alert-warning mt-px-3 mb-3 mt-3 rounded-0">
            {{ system }}
        </div>

        <div class="form-group">
            <label>Meter Number</label>
            <div class="input-group">
                <div class="input-group-prepend">
                    <span
                        :class="['input-group-text input-icon', (meter.name && !meterLoading) ? 'bg-primary text-white' : '']">
                        <i v-if="meterLoading" class="fas fa-spinner fa-pulse"></i>
                        <i v-else-if="meter.name && !meterLoading" class="fas fa-check"></i>
                        <i v-else class="fas fa-calculator"></i>
                    </span>
                </div>
                <input @keyup="showForm" @blur="checkMeterNumber" v-model="form.meter" required type="number"
                       :class="['form-control', form.errors.get('meter') ? 'is-invalid' : '']"
                       placeholder="Enter your ZESA meter number">
                <div v-text="form.errors.get('meter')" class="invalid-feedback"/>
            </div>
        </div>

        <transition name="slide-in-top">
            <div>
                <div v-if="meter.name && !meterLoading" class="alert alert-primary">
                    Meter Name: <strong>{{ meter.name }}</strong> <br>
                    Address: {{ meter.address }} <br>
                    Meter Currency: {{ meter.currency }}
                </div>
                <div class="form-group">
                    <label>Pay with</label>
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <span class="input-group-text input-icon">
                                <i class="fa fa-credit-card"></i>
                            </span>
                        </div>
                        <select v-model="form.payment"
                                :class="['custom-select', form.errors.get('payment') ? 'is-invalid' : '']">
                            <option v-if="meter.currency === 'ZWG'" value="ecocash">EcoCash ZWG</option>
                            <option value="ecocash-usd">EcoCash USD</option>
                            <option value="stripe">International Card</option>
                        </select>
                        <div v-text="form.errors.get('payment')" class="invalid-feedback"/>
                    </div>
                </div>

                <div class="form-group">
                    <label>Amount</label>
                    <div class="input-group ">
                        <div class="input-group-prepend">
                            <span :class="['input-group-text input-icon',form.errors.get('amount') || amountExceedsLimit ? 'border-danger rounded-left border-right-0' : '']">
                                <img :src="flags[methods[form.payment]]" class="currency-icon mr-1" alt="">
                                {{ methods[form.payment] }}
                            </span>
                        </div>
                        <input type="number" v-model="form.amount" required min="0.01" :max="maxLimit" step="any"
                               :class="['form-control', errorClass]"
                               placeholder="Amount">
                        <div v-text="form.errors.get('amount') || amountExceedsLimit" class="invalid-feedback bg-danger rounded-bottom text-white text-center text-1 mt-n1"/>

                    </div>
                    <small>
                        Not sure how much electricity to buy? Use our
                        <router-link to="/zesa-calculator" class="text-danger"><b>ZESA Calculator</b></router-link>
                    </small>
                </div>

                <div v-if="meter.currency === 'USD' && form.payment === 'stripe' && form.amount" class="alert alert-warning">
                    We have added a {{ paymentPremium }}% fee to recover the costs of card processing.
                    This currently applies only to this product. You will therefore be billed {{ totalPayment }} at checkout.
                    <div class="custom-control custom-checkbox">
                        <input type="checkbox" class="custom-control-input" id="terms" required v-model="paymentPremiumAccepted">
                        <label class="custom-control-label font-weight-bold" for="terms">
                            I accept these terms.
                        </label>
                    </div>
                </div>

                <div class="form-group">
                    <label>Recipient's Phone Number</label>
                    <phone-input v-model="form.phone"></phone-input>
                    <div v-text="form.errors.get('phone')" class="invalid-feedback"/>
                    <small>* Number used for notifications</small>
                </div>

                <div class="form-group">
                    <label>Email <span v-if="form.payment === 'stripe'">*</span></label>
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <span class="input-group-text input-icon">
                                <i class="fa fa-envelope"></i>
                            </span>
                        </div>
                        <input type="email" v-model="form.email" value="email" :required="form.payment === 'stripe'"
                               :class="['form-control', form.errors.get('email') ? 'is-invalid' : '']"
                               placeholder="Email">
                        <div v-text="form.errors.get('email')" class="invalid-feedback"/>
                    </div>
                </div>
                <div class="form-group d-flex align-items-center">
                    <input type="checkbox" class="mr-2" id="checkterms" v-model="termsAccepted">
                    <label class="mb-0" for="checkterms">I accept the <a target="_blank" href="/terms">Terms & Conditions</a></label>
                </div>
            </div>
        </transition>
        <div class="form-group">
            <button type="button"
                    v-bind ="buttonValidity"
                    :class="{ 'btn-loading': form.loading, ...buttonValidity.class }"
                    @click="formVisibility ? submit($event) : (formVisibility = true)">
                Buy ZESA
            </button>
        </div>
    </form>
</template>

<script>
import Form from "../../../core/forms/form";
import PhoneInput from "../../custom/phone-input";
import PaymentOptions from "../../custom/payment-options.vue";
import {validity} from "../../../core/forms/validity";
import {appDataMixin, CURRENCY_CURRENT} from "../../../core/data/global";

export default {
    name: "dashboard",
    components: {PhoneInput, PaymentOptions},
    mixins: [validity, appDataMixin],
    data: function () {
        return {
            system: '',
            meterLoading: false,
            amountExceedsLimits:false,
            meterCurrency: 'ZWG',
            paymentPremium: 0,
            limitZWG: 0,
            limitUSD: 0,
            limit:0,
            paymentPremiumAccepted: false,
            fields: [
                'meter',
                'phone',
                'amount',
                'currency',
                'email',
            ],
            methods: {
                'ecocash': 'ZWG',
                'ecocash-usd': 'USD',
                'stripe': 'USD',
            },
            meter: {},
            form: new Form({
                'meter': '',
                'meter_currency': CURRENCY_CURRENT,
                'payment': 'ecocash',
                'phone': '',
                'amount': '',
                'email': '',
            })
        };
    },
    computed: {
        totalPayment(){
            const amount = parseFloat(this.form.amount);
            const premium = 1 + (this.paymentPremium / 100);

            return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount * premium);
        },
        maxLimit() {
            return this.methods[this.form.payment] === 'ZWG' ? this.limitZWG : this.limitUSD;
        },
        errorClass() {
            return this.form.errors.get('amount') || this.amountExceedsLimit ? 'is-invalid border-danger rounded-right border-left-0' : '';
        },
        amountExceedsLimit() {
            if ((this.form.amount > this.limitZWG && this.methods[this.form.payment] === 'ZWG') ||
                (this.form.amount > this.limitUSD && this.methods[this.form.payment] === 'USD')) {
                this.amountExceedsLimits = true;
                return ` ${this.methods[this.form.payment] === 'USD' ? (this.limitUSD !== 0 ? 'Your amount must not exceed the Zesa limit of USD' + this.limitUSD : '') :  (this.limitZWG !== 0 ? 'Your amount must not exceed the Zesa limit of ZWG' + this.limitZWG : '')}`;
            }
            return '';
        },
    },
    methods: {
        checkMeterNumber() {
            this.meter = {};
            this.meterLoading = true;
            this.paymentPremium = 0;
            this.paymentPremiumAccepted = false;
            this.form.submit('/bills/zesaMeter/check').then((response) => {
                this.meter = response.data.body.meter;
                this.form.meter_currency = this.meter.currency;
                this.limitZWG = response.data.body.limitZWG;
                this.limitUSD = response.data.body.limitUSD;

                if (this.meter.currency === 'USD') {
                    this.form.payment = 'stripe';
                    this.paymentPremium = response.data.body.premium;
                }
            }).finally(() => {
                this.meterLoading = false;
            });
        },

        saveToLocalStorage() {
            for (let i = 0; i < this.fields.length; i++) {
                localStorage.setItem(this.fields[i], this.form[this.fields[i]]);
            }
        },

        getFromLocalStorage() {
            for (let i = 0; i < this.fields.length; i++) {

                let value = localStorage.getItem(this.fields[i]);
                if (value != null && value !== "" && value !== "null") {
                    this.form[this.fields[i]] = value;
                }
            }
        },

        check() {
            window.axios.get(`${window.location.origin}/zesa/stability/check`).then((response) => {
                this.system = response.data.body.message;
            });
        },

        submit(event) {
            // Save to Local Storage

            if (this.$refs.form.checkValidity() && !this.form.errors.any()) {
                event.preventDefault()

                this.saveToLocalStorage();
                this.form.submit('/bills/zesa/init').then((response) => {

                    // Redirect based on payment type
                    if (response.data.success) {
                        //this.$route.query.id
                        //this.$route.params.id

                        if (response.data.body.payment.payment === "ecocash" || response.data.body.payment.payment === "ecocash-usd" ) {
                            this.$router.push(`/zesa/ecocash?id=${response.data.body.payment.ref}`);
                        }

                        if (response.data.body.payment.payment === "stripe") {
                            window.location.href = response.data.body.payment.url;
                        }

                    } else {

                    }
                });
            }

        },

        buyFromValid() {
            return !this.form.errors.any() ||
                this.$refs.form &&
                this.$refs.form.checkValidity()
        }
    },
    watch: {
        'form.phone'(n, o) {
            if (n.startsWith('0')) {
                this.form.phone = n.substring(1)
            }
        }
    },

    mounted() {
        // Check in Local Storage

        this.check();
        this.getFromLocalStorage();

        // Check Meter Number

        if (this.form.meter !== "") {
            this.checkMeterNumber();
        }
        this.scrollToTop();

    }
}
</script>
<style scoped>
.form-control.is-invalid,
.was-validated .form-control:invalid {
    border-color: #dc3545;
    border-radius: 0;
}
.amount-exceeded-outline {
    border: 1px solid red;
}
</style>

